import React, { useState } from "react"
import { Formik } from "formik"
import { Box, Typography } from "@mui/material"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import moment from "moment"
import DeliveryModal from "./DeliveryModal"

const DeliveryDistributionModal = ({ open, onClose, onCancel, enterpriseId, enterprises, availableDCs, initialSlots = [], onSubmit }) => {
  const [errorIndices, setErrorIndices] = useState(new Set())
  const [serverError, setServerError] = useState(null)

  const clearError = (idx) => setErrorIndices((prev) => { const next = new Set(prev); next.delete(idx); return next })

  return (
    <Formik
      initialValues={{ enterpriseId, slots: initialSlots }}
      onSubmit={async (values, { setSubmitting }) => {
        const invalid = new Set(values.slots.reduce((acc, s, i) => { if (!s.dcId) acc.push(i); return acc }, []))
        if (invalid.size > 0) { setErrorIndices(invalid); setSubmitting(false); return }
        setErrorIndices(new Set())
        setServerError(null)
        try { await onSubmit(values.slots) } catch { setServerError("Une erreur est survenue, veuillez réessayer") } finally { setSubmitting(false) }
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, isSubmitting, handleSubmit: formikSubmit }) => {
        const enterpriseDCIds = (() => {
          if (!values.enterpriseId) return new Set()
          const ent = enterprises.find((e) => e.objectId === values.enterpriseId)
          if (!ent) return new Set()
          return new Set((ent.distributionCenters || []).map((l) => (l.dc && l.dc.objectId) || l.dc))
        })()

        const dcOptions = availableDCs.filter((dc) => enterpriseDCIds.has(dc.objectId))

        const groupedSlots = (() => {
          const groups = []
          const seen = {}
          values.slots.forEach((slot, idx) => {
            if (!seen[slot.date]) { seen[slot.date] = []; groups.push({ date: slot.date, items: seen[slot.date] }) }
            seen[slot.date].push({ ...slot, idx })
          })
          return groups
        })()

        const handleTagClick = (idx, dcId) => {
          clearError(idx)
          const current = values.slots[idx] && values.slots[idx].dcId
          setFieldValue("slots", values.slots.map((s, i) => (i === idx ? { ...s, dcId: current === dcId ? "" : dcId } : s)))
        }

        return (
          <DeliveryModal
            open={open}
            onClose={onClose}
            title="Ajouter les sites de livraison"
            subtitle="Pour chaque livraison, sélectionnez les sites concernés."
            leftLabel="Retour"
            onLeft={onCancel}
            submitLabel="Créer la carte"
            isSubmitType
            onFormSubmit={formikSubmit}
            isSubmitting={isSubmitting}
            dialogSx={{ "& .MuiDialog-paper": { minWidth: 600 } }}
          >
            {serverError && <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>{serverError}</Typography>}
            <Box sx={sx.blocks}>
              {groupedSlots.map(({ date, items }) => {
                const dayLabel = moment(date).format("dddd D MMMM")
                return items.map(({ idx, dcId }, i) => {
                  const hasError = errorIndices.has(idx)
                  const title = items.length > 1 ? `Livraison du ${dayLabel} · Commande ${i + 1}` : `Livraison du ${dayLabel}`
                  return (
                    <Box key={idx} sx={{ ...sx.block, ...(hasError && sx.blockError) }}>
                      <Box sx={sx.blockHead}>
                        <LocalShippingIcon sx={sx.blockIcon} aria-hidden="true" />
                        <Typography sx={sx.blockTitle}>{title}</Typography>
                      </Box>
                      <Box role="group" aria-label={`Sites livrés — ${title}`} sx={sx.tags}>
                        {dcOptions.map((dc) => (
                          <Box key={dc.objectId} component="button" type="button" onClick={() => handleTagClick(idx, dc.objectId)} aria-pressed={dcId === dc.objectId} sx={{ ...sx.tag, ...(dcId === dc.objectId && sx.tagSelected) }}>
                            {dc.name}
                          </Box>
                        ))}
                        {dcOptions.length === 0 && <Typography variant="caption" color="text.disabled">Aucun site configuré pour cette entreprise</Typography>}
                      </Box>
                      {hasError && <Typography variant="caption" color="error">Veuillez sélectionner un site de livraison</Typography>}
                    </Box>
                  )
                })
              })}
            </Box>
          </DeliveryModal>
        )
      }}
    </Formik>
  )
}

const sx = {
  blocks: { display: "flex", flexDirection: "column", gap: "16px" },
  block: {
    borderRadius: "4px",
    border: "1px solid",
    borderColor: "divider",
    p: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  blockError: { borderColor: "error.main" },
  blockHead: { display: "flex", alignItems: "center", gap: "8px" },
  blockIcon: { fontSize: 18, color: "text.secondary", flexShrink: 0 },
  blockTitle: { fontSize: 14, fontWeight: 500, color: "text.primary" },
  tags: { display: "flex", flexWrap: "wrap", gap: "6px" },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    px: "12px",
    py: "6px",
    borderRadius: "999px",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "transparent",
    color: "text.primary",
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    transition: "border-color 120ms, background-color 120ms, color 120ms",
    "&:hover": { borderColor: "primary.main", color: "primary.main" },
  },
  tagSelected: {
    borderColor: "primary.main",
    bgcolor: "primary.main",
    color: "white",
    "&:hover": { bgcolor: "primary.dark", borderColor: "primary.dark", color: "white" },
  },
}

export default DeliveryDistributionModal
